import { Breakpoints, Palette } from '@mui/material/styles'
import { TypographyOptions } from '@mui/material/styles/createTypography'

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  export interface TypographyPropsVariantOverrides {
    bodyExtraLarge: true
    bodyExtraSmall: true
    bodyLarge: true
    bodyMedium: true
    bodySmall: true
    buttonLarge: true
    buttonMedium: true
    buttonSmall: true
    captionLarge: true
    captionMedium: true
    captionSmall: true
    overlineExtraLarge: true
    overlineLarge: true
    overlineMedium: true
    overlineSmall: true
    subtitle3: true
  }
}
// Update the TypographyOptions to include the new typography variants
declare module '@mui/material/styles/createTypography' {
  export interface TypographyOptions {
    bodyExtraLarge?: React.CSSProperties
    bodyExtraSmall?: React.CSSProperties
    bodyLarge?: React.CSSProperties
    bodyMedium?: React.CSSProperties
    bodySmall?: React.CSSProperties
    buttonLarge?: React.CSSProperties
    buttonMedium?: React.CSSProperties
    buttonSmall?: React.CSSProperties
    captionLarge?: React.CSSProperties
    captionMedium?: React.CSSProperties
    captionSmall?: React.CSSProperties
    overlineExtraLarge?: React.CSSProperties
    overlineLarge?: React.CSSProperties
    overlineMedium?: React.CSSProperties
    overlineSmall?: React.CSSProperties
    subtitle3?: React.CSSProperties
  }
}
// Preferred Web Safe Font Fallbacks: Arial Black, sans-serif
export const TradeGothicBold = 'Trade Gothic Bold No2'

// Preferred Web Safe Font Fallbacks: Arial Narrow, sans-serif
export const TradeGothicBoldCondensed = 'Trade Gothic Bold Condensed No20'

// Preferred Web Safe Font Fallbacks: Verdana, sans-serif
export const Montserrat = 'Montserrat'

// Preferred Web Safe Font Fallbacks: Verdana, sans-serif
export const MontserratRegular = 'Montserrat Regular'

// Preferred Web Safe Font Fallbacks: Verdana, sans-serif
export const MontserratSemiBold = 'Montserrat SemiBold'

// Preferred Web Safe Font Fallbacks: Verdana, sans-serif
export const MontserratBold = 'Montserrat Bold'

// Preferred Web Safe Font Fallbacks: Verdana, sans-serif
export const MontserratItalic = 'Montserrat Italic'

export const bodyFontFamilies = `${MontserratRegular}, Verdana, sans-serif`
export const bodyBoldFontFamilies = `${MontserratBold}, Verdana, sans-serif`
export const bodyItalicFontFamilies = `${MontserratItalic}, Verdana, sans-serif`
export const buttonFontFamilies = `${MontserratSemiBold}, Verdana, sans-serif`
export const headingFontFamilies = `${TradeGothicBoldCondensed}, Arial Narrow, sans-serif`
export const subtitleFontFamilies = `${TradeGothicBold}, Arial Black, sans-serif`
export const overlineFontFamilies = `${MontserratSemiBold}, Verdana, sans-serif`

export const defaultHeadingStyles: React.CSSProperties = {
  fontFamily: headingFontFamilies,
  fontWeight: 700,
  lineHeight: '1.05',
  textTransform: 'uppercase',
}

// standard subtitle styles, which can be consistently applied between these elements
export const defaultSubtitleStyles: React.CSSProperties = {
  fontFamily: subtitleFontFamilies,
  fontWeight: 700,
  letterSpacing: '0rem',
  lineHeight: '1.25',
  textTransform: 'initial',
}

// standard body/p styles, which can be consistently applied between these elements
export const defaultBodyStyles: React.CSSProperties = {
  fontFamily: bodyFontFamilies,
  fontWeight: 400,
  letterSpacing: '0rem',
  lineHeight: '1.65',
  textTransform: 'initial',
}

// standard button styles, which can be consistently applied between these elements
export const defaultButtonStyles: React.CSSProperties = {
  fontFamily: buttonFontFamilies,
  fontWeight: 600,
  textTransform: 'uppercase',
}

// standard caption styles, which can be consistently applied between these elements
export const defaultCaptionStyles: React.CSSProperties = {
  fontFamily: bodyFontFamilies,
  fontWeight: 400,
  letterSpacing: '0.03125rem',
  lineHeight: '1.15',
  textTransform: 'uppercase',
}

// standard overline styles, which can be consistently applied between these elements
export const defaultOverlineStyles: React.CSSProperties = {
  fontFamily: overlineFontFamilies,
  fontWeight: 600,
  lineHeight: '1.15',
  textTransform: 'uppercase',
}

export const typography = (palette: Palette, breakpoints: Breakpoints): TypographyOptions => {
  /**
   * theme.typography: body styles
   */

  // depicted in the style guide as 'Body Extra Large'
  const bodyExtraLarge = {
    ...defaultBodyStyles,
    color: palette.text.primary,
    fontSize: '1.125rem',

    '& strong, & b': {
      fontFamily: bodyBoldFontFamilies,
    },

    '& em, & i': {
      fontFamily: bodyItalicFontFamilies,
    },
  }

  // depicted in the style guide as 'Body Large'
  const bodyLarge = {
    ...defaultBodyStyles,
    color: palette.text.primary,
    fontSize: '1rem',

    '& strong, & b': {
      fontFamily: bodyBoldFontFamilies,
    },

    '& em, & i': {
      fontFamily: bodyItalicFontFamilies,
    },
  }

  // depicted in the style guide as 'Body Medium'
  const bodyMedium = {
    ...defaultBodyStyles,
    color: palette.text.primary,
    fontSize: '0.875rem',

    '& strong, & b': {
      fontFamily: bodyBoldFontFamilies,
    },

    '& em, & i': {
      fontFamily: bodyItalicFontFamilies,
    },
  }

  // depicted in the style guide as 'Body Small'
  const bodySmall = {
    ...defaultBodyStyles,
    color: palette.text.primary,
    fontSize: '0.75rem',

    '& strong, & b': {
      fontFamily: bodyBoldFontFamilies,
    },

    '& em, & i': {
      fontFamily: bodyItalicFontFamilies,
    },
  }

  // depicted in the style guide as 'Body Extra Small'
  const bodyExtraSmall = {
    ...defaultBodyStyles,
    color: palette.text.primary,
    fontSize: '0.625rem',

    '& strong, & b': {
      fontFamily: bodyBoldFontFamilies,
    },

    '& em, & i': {
      fontFamily: bodyItalicFontFamilies,
    },
  }

  /**
   * typography: h1-h6 styles
   */

  // depicted in the style guide as 'H1'
  const h1 = {
    ...defaultHeadingStyles,
    color: palette.text.primary,
    fontSize: '4.375rem',
    letterSpacing: '-0.09375rem',
    [breakpoints.down('md')]: {
      fontSize: '2.9375rem',
    },
  }

  // depicted in the style guide as 'H2'
  const h2 = {
    ...defaultHeadingStyles,
    color: palette.text.primary,
    fontSize: '3.625rem',
    letterSpacing: '-0.09375rem',
    [breakpoints.down('md')]: {
      fontSize: '2.375rem',
      letterSpacing: '-0.0625rem',
    },
  }

  // depicted in the style guide as 'H3'
  const h3 = {
    ...defaultHeadingStyles,
    color: palette.text.primary,
    fontSize: '2.9375rem',
    letterSpacing: '-0.0625rem',
    [breakpoints.down('md')]: {
      fontSize: '2rem',
      letterSpacing: '-0.03125rem',
    },
  }

  // depicted in the style guide as 'H4'
  const h4 = {
    ...defaultHeadingStyles,
    color: palette.text.primary,
    fontSize: '2.25rem',
    letterSpacing: '-0.0625rem',
    [breakpoints.down('md')]: {
      fontSize: '1.625rem',
      letterSpacing: '-0.03125rem',
    },
  }

  // depicted in the style guide as 'H5'
  const h5 = {
    ...defaultHeadingStyles,
    color: palette.text.primary,
    fontSize: '1.625rem',
    letterSpacing: '-0.03125rem',
    [breakpoints.down('md')]: {
      fontSize: '1.25rem',
      letterSpacing: '0rem',
    },
  }

  // depicted in the style guide as 'H6'
  const h6 = {
    ...defaultHeadingStyles,
    color: palette.text.primary,
    fontSize: '1.25rem',
    letterSpacing: '0rem',
    [breakpoints.down('md')]: {
      fontSize: '1rem',
    },
  }

  /**
   * typography: button styles
   */

  const buttonLarge = {
    ...defaultButtonStyles,
    color: palette.text.primary,
    fontSize: '0.875rem',
    lineHeight: '1',
    letterSpacing: '0.0625rem',
  }

  const buttonMedium = {
    ...defaultButtonStyles,
    color: palette.text.primary,
    fontSize: '0.75rem',
    lineHeight: '1',
    letterSpacing: '0.0625rem',
  }

  const buttonSmall = {
    ...defaultButtonStyles,
    color: palette.text.primary,
    fontSize: '0.625rem',
    lineHeight: '1',
    letterSpacing: '0.0625rem',
  }

  /**
   * typography: caption styles
   */
  const captionLarge = {
    ...defaultCaptionStyles,
    color: palette.text.primary,
    fontSize: '0.875rem',
  }

  const captionMedium = {
    ...defaultCaptionStyles,
    color: palette.text.primary,
    fontSize: '0.75rem',
  }

  const captionSmall = {
    ...defaultCaptionStyles,
    color: palette.text.primary,
    fontSize: '0.625rem',
  }

  /**
   * typography: overline styles
   */
  const overlineExtraLarge = {
    ...defaultOverlineStyles,
    color: palette.text.primary,
    fontSize: '1rem',
    letterSpacing: '0.0625rem',
  }

  const overlineLarge = {
    ...defaultOverlineStyles,
    color: palette.text.primary,
    fontSize: '0.875rem',
    letterSpacing: '0.0625rem',
  }

  const overlineMedium = {
    ...defaultOverlineStyles,
    color: palette.text.primary,
    fontSize: '0.75rem',
    letterSpacing: '0.0625rem',
  }

  const overlineSmall = {
    ...defaultOverlineStyles,
    color: palette.text.primary,
    fontSize: '0.625rem',
    letterSpacing: '0.0625rem',
  }

  /**
   * typography: subtitle styles
   */
  const subtitle1 = {
    ...defaultSubtitleStyles,
    color: palette.text.primary,
    fontSize: '1.375rem',
  }

  const subtitle2 = {
    ...defaultSubtitleStyles,
    color: palette.text.primary,
    fontSize: '1rem',
  }

  const subtitle3 = {
    ...defaultSubtitleStyles,
    color: palette.text.primary,
    fontSize: '0.875rem',
  }

  return {
    /**
     * The following typography variants have been organized into
     * three categories (common, custom, and default MUI)
     * Refer to the description above each category for more details
     */
    ...bodyMedium,
    fontSize: 16, // base font is set in px, everything else should use em's or rem's
    /**
     * Common MUI/Wayshrine Typography Variants
     *
     * The following variants are common between Material-UI and the Wayshrine Design System
     */
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    subtitle1,
    subtitle2,
    /**
     * Custom Typography Variants
     *
     * The following variants are specific to the Wayshrine Design System
     */
    subtitle3,
    bodyExtraLarge,
    bodyLarge,
    bodyMedium,
    bodySmall,
    bodyExtraSmall,
    buttonLarge,
    buttonMedium,
    buttonSmall,
    captionLarge,
    captionMedium,
    captionSmall,
    overlineExtraLarge,
    overlineLarge,
    overlineMedium,
    overlineSmall,
    /**
     * Material-UI Typography Variants
     *
     * The following variants are the remaining MUI typography defaults
     * (overridden with select Wayshrine values)
     * We will mostly (if not solely) use common mui
     * and wayshrine specific typography variants in our components
     * (per the designs), but it's important to include these variants
     * to add wayshrine theme support for built-in MUI / third party components
     */
    body1: bodyMedium,
    body2: bodyMedium,
    button: buttonMedium,
    caption: captionMedium,
    overline: overlineLarge,
  }
}

export const typographyComponent = {
  defaultProps: {
    variantMapping: {
      h1: 'h1',
      h2: 'h2',
      h3: 'h3',
      h4: 'h4',
      h5: 'h5',
      h6: 'h6',

      // Subtitle
      subtitle1: 'h6',
      subtitle2: 'h6',
      subtitle3: 'h6',

      // Body
      bodyExtraLarge: 'p',
      bodyLarge: 'p',
      bodyMedium: 'p',
      bodySmall: 'p',
      bodyExtraSmall: 'p',

      // Button
      buttonLarge: 'span',
      buttonMedium: 'span',
      buttonSmall: 'span',

      // Caption
      captionLarge: 'span',
      captionMedium: 'span',
      captionSmall: 'span',

      // Overline
      overlineExtraLarge: 'span',
      overlineLarge: 'span',
      overlineMedium: 'span',
      overlineSmall: 'span',
    },
  },
}
